/**
 * Jobs, drawings, measurements, appointments, estimates and the sales settings. Query keys live
 * under ["tenant", slug, "sales"] so a change (a published drawing, an issued estimate) refreshes
 * every view that shows it.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ensureCsrfCookie, unwrap } from "../client";
import { toApiError } from "../errors";
import type { components } from "../generated/schema";
import { noErrorToast } from "../mutationMeta";
import type { Answer, Body } from "./operations";

type S = components["schemas"];
const path = (slug: string) => ({ path: { tenant_slug: slug } });
const pathId = (slug: string, id: string) => ({ path: { tenant_slug: slug, id } });

export const salesKeys = {
  all: (slug: string) => ["tenant", slug, "sales"] as const,
  jobs: (slug: string) => ["tenant", slug, "sales", "jobs"] as const,
  drawings: (slug: string) => ["tenant", slug, "sales", "drawings"] as const,
  estimates: (slug: string) => ["tenant", slug, "sales", "estimates"] as const,
  appointments: (slug: string) => ["tenant", slug, "sales", "appointments"] as const,
  settings: (slug: string) => ["tenant", slug, "sales", "settings"] as const,
};

export type JobRow = S["JobList"];
export type Job = S["JobDetail"];
export type JobWrite = Body<"/api/t/{tenant_slug}/jobs/", "post">;
export type Activity = S["Activity"];
export type JobPurchasing = S["JobPurchasing"];
export type DrawingRow = S["DrawingList"];
export type Drawing = S["DrawingDetail"];
export type RevisionBrief = S["RevisionBrief"];
export type DrawingRevision = S["RevisionDetail"];
export type DrawingFile = S["DrawingFile"];
export type ReferenceInspection = S["ReferenceInspection"];
export type ReferenceImportResult = S["ReferenceImportResult"];
export type Measurement = S["Measurement"];
export type TakeoffRow = S["TakeoffRow"];
export type DrawingItem = S["DrawingItem"];
export type Appointment = S["Appointment"];
export type EstimateRow = S["EstimateList"];
export type Estimate = S["EstimateDetail"];
export type EstimateRevision = S["Revision"];
export type EstimateLine = S["EstimateLine"];
export type DrawingPlan = S["DrawingPlanResult"];
export type SalesSettings = S["SalesSettings"];
export type DetailItem = S["DetailItem"];
export type TaxRate = S["TaxRate"];
export type JobType = S["JobType"];
export type RequirementRule = S["RequirementRule"];
export type ProposalLink = S["ProposalLink"];
export type PublicProposal = S["PublicProposal"];

// --- settings ------------------------------------------------------------------------------------
export function useSalesSettings(slug: string) {
  return useQuery({
    queryKey: [...salesKeys.settings(slug), "settings"],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/sales-settings/", { params: path(slug) })),
    staleTime: 60_000,
  });
}

export function useSaveSalesSettings(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: Body<"/api/t/{tenant_slug}/sales-settings/", "patch">) =>
      unwrap(api.PATCH("/api/t/{tenant_slug}/sales-settings/", { params: path(slug), body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: salesKeys.settings(slug) }),
  });
}

export function useTaxRates(slug: string) {
  return useQuery({
    queryKey: [...salesKeys.settings(slug), "tax-rates"],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/tax-rates/", { params: path(slug) })),
    staleTime: 60_000,
  });
}

export function useJobTypes(slug: string) {
  return useQuery({
    queryKey: [...salesKeys.settings(slug), "job-types"],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/job-types/", { params: path(slug) })),
    staleTime: 60_000,
  });
}

export function useRequirementRules(slug: string) {
  return useQuery({
    queryKey: [...salesKeys.settings(slug), "rules"],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/requirement-rules/", { params: path(slug) })),
  });
}

/** Every countertop detail a drawing can show, with the company's item for it (or none). */
export function useDetailItems(slug: string) {
  return useQuery({
    queryKey: [...salesKeys.settings(slug), "detail-items"],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/detail-items/", { params: path(slug) })),
    staleTime: 60_000,
  });
}

/** Price a detail with an item (or stop pricing it); drawings' quantities follow. */
export function useSetDetailItem(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: Body<"/api/t/{tenant_slug}/detail-items/", "post">) => unwrap(api.POST("/api/t/{tenant_slug}/detail-items/", { params: path(slug), body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.settings(slug) });
      void qc.invalidateQueries({ queryKey: [...salesKeys.drawings(slug), "takeoff"] });
    },
  });
}

export function useSettingsWrite(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (
      vars:
        | { kind: "tax"; id?: string; body: Body<"/api/t/{tenant_slug}/tax-rates/", "post"> }
        | { kind: "job-type"; id?: string; body: Body<"/api/t/{tenant_slug}/job-types/", "post"> }
        | { kind: "rule"; id?: string; body: Body<"/api/t/{tenant_slug}/requirement-rules/", "post"> },
    ): Promise<unknown> => {
      if (vars.kind === "tax")
        return vars.id
          ? unwrap(api.PATCH("/api/t/{tenant_slug}/tax-rates/{id}/", { params: pathId(slug, vars.id), body: vars.body }))
          : unwrap(api.POST("/api/t/{tenant_slug}/tax-rates/", { params: path(slug), body: vars.body }));
      if (vars.kind === "job-type")
        return vars.id
          ? unwrap(api.PATCH("/api/t/{tenant_slug}/job-types/{id}/", { params: pathId(slug, vars.id), body: vars.body }))
          : unwrap(api.POST("/api/t/{tenant_slug}/job-types/", { params: path(slug), body: vars.body }));
      return vars.id
        ? unwrap(api.PATCH("/api/t/{tenant_slug}/requirement-rules/{id}/", { params: pathId(slug, vars.id), body: vars.body }))
        : unwrap(api.POST("/api/t/{tenant_slug}/requirement-rules/", { params: path(slug), body: vars.body }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: salesKeys.settings(slug) }),
  });
}

// --- jobs --------------------------------------------------------------------------------------------
export type JobFilters = { status?: string; open?: string; client?: string; member?: string; mine?: string; q?: string; ordering?: string; page?: number };

export function useJobs(slug: string, filters: JobFilters, enabled = true) {
  return useQuery({
    queryKey: [...salesKeys.jobs(slug), "list", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/jobs/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useJob(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...salesKeys.jobs(slug), "detail", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/jobs/{id}/", { params: pathId(slug, id!) })),
  });
}

/** Can the job close? Its open work and (with billing.view) its open money, separately (ADR-042). */
export function useJobCloseout(slug: string, id: string, enabled = true) {
  return useQuery({
    queryKey: [...salesKeys.jobs(slug), "closeout", id],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/jobs/{id}/closeout/", { params: pathId(slug, id) })),
  });
}

export function useJobTimeline(slug: string, id: string, page: number) {
  return useQuery({
    queryKey: [...salesKeys.all(slug), "timeline", id, page],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/jobs/{id}/timeline/", { params: { ...pathId(slug, id), query: { page } } })),
    placeholderData: keepPreviousData,
  });
}

export function useJobPurchasing(slug: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...salesKeys.jobs(slug), "purchasing", id],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/jobs/{id}/purchasing/", { params: pathId(slug, id) })),
  });
}

export function useJobAttachments(slug: string, id: string) {
  return useQuery({
    queryKey: [...salesKeys.jobs(slug), "attachments", id],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/jobs/{id}/attachments/", { params: pathId(slug, id) })),
  });
}

export function useJobAction(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (
      vars:
        | { action: "create"; body: JobWrite }
        | { action: "update"; id: string; body: Body<"/api/t/{tenant_slug}/jobs/{id}/", "patch"> }
        | { action: "status"; id: string; body: Body<"/api/t/{tenant_slug}/jobs/{id}/status/", "post"> }
        | { action: "contact"; id: string; body: Body<"/api/t/{tenant_slug}/jobs/{id}/contacts/", "post"> }
        | { action: "assignment"; id: string; body: Body<"/api/t/{tenant_slug}/jobs/{id}/assignments/", "post"> }
        | { action: "remove-contact"; id: string }
        | { action: "remove-assignment"; id: string }
        | { action: "link"; id: string; body: Body<"/api/t/{tenant_slug}/jobs/{id}/link-purchasing/", "post"> },
    ): Promise<unknown> => {
      switch (vars.action) {
        case "create":
          return unwrap(api.POST("/api/t/{tenant_slug}/jobs/", { params: path(slug), body: vars.body }));
        case "update":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/jobs/{id}/", { params: pathId(slug, vars.id), body: vars.body }));
        case "status":
          return unwrap(api.POST("/api/t/{tenant_slug}/jobs/{id}/status/", { params: pathId(slug, vars.id), body: vars.body }));
        case "contact":
          return unwrap(api.POST("/api/t/{tenant_slug}/jobs/{id}/contacts/", { params: pathId(slug, vars.id), body: vars.body }));
        case "assignment":
          return unwrap(api.POST("/api/t/{tenant_slug}/jobs/{id}/assignments/", { params: pathId(slug, vars.id), body: vars.body }));
        case "remove-contact":
          return unwrap(api.DELETE("/api/t/{tenant_slug}/job-contacts/{id}/", { params: pathId(slug, vars.id) }));
        case "remove-assignment":
          return unwrap(api.DELETE("/api/t/{tenant_slug}/job-assignments/{id}/", { params: pathId(slug, vars.id) }));
        default:
          return unwrap(api.POST("/api/t/{tenant_slug}/jobs/{id}/link-purchasing/", { params: pathId(slug, vars.id), body: vars.body }));
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: salesKeys.all(slug) }),
  });
}

// --- drawings ---------------------------------------------------------------------------------------
/** `estimate` / `sales_order`: the drawings that document can use (its own, its job's, an order's estimate's). */
export type DrawingFilters = { job?: string; estimate?: string; sales_order?: string; client?: string; kind?: string; purpose?: string; status?: string; q?: string; page?: number; page_size?: number };

export function useDrawings(slug: string, filters: DrawingFilters, enabled = true) {
  return useQuery({
    queryKey: [...salesKeys.drawings(slug), "list", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/drawings/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useDrawing(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...salesKeys.drawings(slug), "detail", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/drawings/{id}/", { params: pathId(slug, id!) })),
  });
}

export function useDrawingRevision(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...salesKeys.drawings(slug), "revision", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/drawing-revisions/{id}/", { params: pathId(slug, id!) })),
    // A draft's geometry belongs to the editor once loaded: never refetch it under the person's hands.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useTakeoff(slug: string, id: string | undefined, version: number | undefined) {
  return useQuery({
    queryKey: [...salesKeys.drawings(slug), "takeoff", id, version],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/drawing-revisions/{id}/takeoff/", { params: pathId(slug, id!) })),
    placeholderData: keepPreviousData,
  });
}

export function useDrawingMeasurements(slug: string, drawingId: string | undefined) {
  return useQuery({
    queryKey: [...salesKeys.drawings(slug), "measurements", drawingId],
    enabled: Boolean(drawingId),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/drawings/{id}/measurements/", { params: pathId(slug, drawingId!) })),
  });
}

export function useRevisionCompare(slug: string, id: string | undefined, against: string | undefined) {
  return useQuery({
    queryKey: [...salesKeys.drawings(slug), "compare", id, against],
    enabled: Boolean(id && against),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/drawing-revisions/{id}/compare/", { params: { ...pathId(slug, id!), query: { against: against! } } })),
  });
}

export function useDrawingItems(slug: string, q: string, ids?: string[], enabled = true) {
  return useQuery({
    queryKey: [...salesKeys.drawings(slug), "items", q, ids?.join(",")],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/drawing-items/", { params: { ...path(slug), query: { q: q || undefined, ids: ids?.length ? ids.join(",") : undefined, page_size: ids?.length ? 100 : 20 } as never } })),
    placeholderData: keepPreviousData,
  });
}

/**
 * Items a work group's choice may pick: sold, active and within the choice's product type branch.
 * `kind` narrows them to item kinds ("service": labour and services; "material,allowance").
 */
export function useChoiceItems(slug: string, q: string, category: string | undefined, enabled = true, kind?: string) {
  return useQuery({
    queryKey: [...salesKeys.drawings(slug), "items", "choice", category, q, kind],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/drawing-items/", { params: { ...path(slug), query: { q: q || undefined, category, kind, page_size: 20 } as never } })),
    placeholderData: keepPreviousData,
  });
}

export type DrawingPiece = S["DrawingPiece"];

/** An item's slabs in stock now, with their photos (needs inventory.view_stock). */
export function useDrawingPieces(slug: string, itemId: string, q: string, enabled = true) {
  return useQuery({
    queryKey: [...salesKeys.drawings(slug), "pieces", itemId, q],
    enabled: enabled && !!itemId,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/drawing-items/pieces/", { params: { ...path(slug), query: { item: itemId, q: q || undefined } } })),
    placeholderData: keepPreviousData,
  });
}

/** The slabs a drawing shows, wherever they are now. */
export function usePickedPieces(slug: string, ids: string[]) {
  return useQuery({
    queryKey: [...salesKeys.drawings(slug), "picked-pieces", ids.join(",")],
    enabled: ids.length > 0,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/drawing-items/picked-pieces/", { params: { ...path(slug), query: { ids: ids.join(",") } } })),
  });
}

/** Saving a draft (autosave): never toasts by itself; the editor shows the save state. */
export function useSaveDraft(slug: string) {
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: { id: string; body: Body<"/api/t/{tenant_slug}/drawing-revisions/{id}/save/", "post"> }) =>
      unwrap(api.POST("/api/t/{tenant_slug}/drawing-revisions/{id}/save/", { params: pathId(slug, vars.id), body: vars.body })),
  });
}

/**
 * Merge an uploaded DXF's mapped layers into a draft as locked reference layers (ADR-018). Nothing
 * is refetched here: the editor reloads the draft when the person has read the report.
 */
export function useImportReference(slug: string) {
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: { id: string; body: Body<"/api/t/{tenant_slug}/drawing-revisions/{id}/import-reference/", "post"> }) =>
      unwrap(api.POST("/api/t/{tenant_slug}/drawing-revisions/{id}/import-reference/", { params: pathId(slug, vars.id), body: vars.body })),
  });
}

export function useDrawingAction(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (
      vars:
        | { action: "create"; body: Body<"/api/t/{tenant_slug}/drawings/", "post"> }
        | { action: "update"; id: string; body: Body<"/api/t/{tenant_slug}/drawings/{id}/", "patch"> }
        | { action: "archive" | "restore"; id: string }
        | { action: "start-draft"; id: string; body: Body<"/api/t/{tenant_slug}/drawings/{id}/start-draft/", "post"> }
        | { action: "publish"; id: string; body: Body<"/api/t/{tenant_slug}/drawing-revisions/{id}/publish/", "post"> }
        | { action: "discard"; id: string }
        | { action: "decide"; id: string; body: Body<"/api/t/{tenant_slug}/drawing-revisions/{id}/decide/", "post"> }
        | { action: "verify"; id: string; body: Body<"/api/t/{tenant_slug}/drawing-revisions/{id}/verify/", "post"> }
        | { action: "calibrate"; id: string; body: Body<"/api/t/{tenant_slug}/drawing-revisions/{id}/calibrate/", "post"> }
        | { action: "measure"; id: string; body: Body<"/api/t/{tenant_slug}/drawing-revisions/{id}/measurements/", "post"> }
        | { action: "decide-measurement"; id: string; body: Body<"/api/t/{tenant_slug}/measurements/{id}/decide/", "post"> }
        | { action: "import"; id: string; body: Body<"/api/t/{tenant_slug}/drawing-files/{id}/import/", "post"> }
        | { action: "trace"; id: string; body: Body<"/api/t/{tenant_slug}/drawing-files/{id}/trace/", "post"> },
    ): Promise<unknown> => {
      const p = "id" in vars ? pathId(slug, vars.id) : path(slug);
      switch (vars.action) {
        case "create":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawings/", { params: path(slug), body: vars.body }));
        case "update":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/drawings/{id}/", { params: pathId(slug, vars.id), body: vars.body }));
        case "archive":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawings/{id}/archive/", { params: pathId(slug, vars.id) }));
        case "restore":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawings/{id}/restore/", { params: pathId(slug, vars.id) }));
        case "start-draft":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawings/{id}/start-draft/", { params: pathId(slug, vars.id), body: vars.body }));
        case "publish":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawing-revisions/{id}/publish/", { params: pathId(slug, vars.id), body: vars.body }));
        case "discard":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawing-revisions/{id}/discard/", { params: pathId(slug, vars.id) }));
        case "decide":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawing-revisions/{id}/decide/", { params: pathId(slug, vars.id), body: vars.body }));
        case "verify":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawing-revisions/{id}/verify/", { params: pathId(slug, vars.id), body: vars.body }));
        case "calibrate":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawing-revisions/{id}/calibrate/", { params: pathId(slug, vars.id), body: vars.body }));
        case "measure":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawing-revisions/{id}/measurements/", { params: pathId(slug, vars.id), body: vars.body }));
        case "decide-measurement":
          return unwrap(api.POST("/api/t/{tenant_slug}/measurements/{id}/decide/", { params: pathId(slug, vars.id), body: vars.body }));
        case "import":
          return unwrap(api.POST("/api/t/{tenant_slug}/drawing-files/{id}/import/", { params: pathId(slug, vars.id), body: vars.body }));
        default:
          void p;
          return unwrap(api.POST("/api/t/{tenant_slug}/drawing-files/{id}/trace/", { params: pathId(slug, vars.id), body: vars.body }));
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: salesKeys.all(slug) }),
  });
}

/** Multipart uploads (drawing documents, new document revisions, job and visit files). */
export async function postForm<T>(url: string, fields: Record<string, string | File | undefined | null>): Promise<T> {
  await ensureCsrfCookie();
  const { getCookie } = await import("@/lib/cookies");
  const body = new FormData();
  for (const [key, value] of Object.entries(fields)) if (value !== undefined && value !== null && value !== "") body.append(key, value);
  let response: Response;
  try {
    response = await fetch(url, { method: "POST", body, credentials: "same-origin", headers: { "X-CSRFToken": getCookie("kshop_csrftoken") ?? "" } });
  } catch {
    throw toApiError(0, null);
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) throw toApiError(response.status, data);
  return data as T;
}

export function useInvalidateSales(slug: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: salesKeys.all(slug) });
}

// --- appointments ------------------------------------------------------------------------------------
export type AppointmentFilters = { job?: string; specialist?: string; mine?: string; status?: string; from?: string; to?: string; page?: number };

export function useAppointments(slug: string, filters: AppointmentFilters, enabled = true) {
  return useQuery({
    queryKey: [...salesKeys.appointments(slug), "list", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/appointments/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useAppointment(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...salesKeys.appointments(slug), "detail", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/appointments/{id}/", { params: pathId(slug, id!) })),
  });
}

export function useAppointmentAttachments(slug: string, id: string) {
  return useQuery({
    queryKey: [...salesKeys.appointments(slug), "attachments", id],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/appointments/{id}/attachments/", { params: pathId(slug, id) })),
  });
}

export function useAppointmentConflicts(slug: string, q: { specialist?: string; starts_at?: string; ends_at?: string; exclude?: string }) {
  return useQuery({
    queryKey: [...salesKeys.appointments(slug), "conflicts", q],
    enabled: Boolean(q.specialist && q.starts_at && q.ends_at),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/appointments/conflicts/", { params: { ...path(slug), query: q as { specialist: string; starts_at: string; ends_at: string } } })),
  });
}

export function useAppointmentAction(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (
      vars:
        | { action: "create"; body: Body<"/api/t/{tenant_slug}/appointments/", "post"> }
        | { action: "reschedule"; id: string; body: Body<"/api/t/{tenant_slug}/appointments/{id}/reschedule/", "post"> }
        | { action: "cancel" | "missed"; id: string; body: Body<"/api/t/{tenant_slug}/appointments/{id}/cancel/", "post"> }
        | { action: "confirm"; id: string; body: Body<"/api/t/{tenant_slug}/appointments/{id}/confirm/", "post"> }
        | { action: "submit"; id: string; body: Body<"/api/t/{tenant_slug}/appointments/{id}/submit/", "post"> }
        | { action: "complete"; id: string },
    ): Promise<unknown> => {
      switch (vars.action) {
        case "create":
          return unwrap(api.POST("/api/t/{tenant_slug}/appointments/", { params: path(slug), body: vars.body }));
        case "reschedule":
          return unwrap(api.POST("/api/t/{tenant_slug}/appointments/{id}/reschedule/", { params: pathId(slug, vars.id), body: vars.body }));
        case "cancel":
          return unwrap(api.POST("/api/t/{tenant_slug}/appointments/{id}/cancel/", { params: pathId(slug, vars.id), body: vars.body }));
        case "missed":
          return unwrap(api.POST("/api/t/{tenant_slug}/appointments/{id}/missed/", { params: pathId(slug, vars.id), body: vars.body }));
        case "confirm":
          return unwrap(api.POST("/api/t/{tenant_slug}/appointments/{id}/confirm/", { params: pathId(slug, vars.id), body: vars.body }));
        case "submit":
          return unwrap(api.POST("/api/t/{tenant_slug}/appointments/{id}/submit/", { params: pathId(slug, vars.id), body: vars.body }));
        default:
          return unwrap(api.POST("/api/t/{tenant_slug}/appointments/{id}/complete/", { params: pathId(slug, vars.id) }));
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: salesKeys.all(slug) }),
  });
}

// --- estimates --------------------------------------------------------------------------------------
export type EstimateFilters = { status?: string; type?: string; client?: string; job?: string; phase?: string; estimator?: string; mine?: string; q?: string; page?: number };

export function useEstimates(slug: string, filters: EstimateFilters, enabled = true) {
  return useQuery({
    queryKey: [...salesKeys.estimates(slug), "list", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/estimates/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

/** How many estimates each list view holds, with the list's other filters. */
export function useEstimateCounts(slug: string, filters: Omit<EstimateFilters, "status" | "page">) {
  return useQuery({
    queryKey: [...salesKeys.estimates(slug), "counts", filters],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/estimates/counts/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useEstimate(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...salesKeys.estimates(slug), "detail", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/estimates/{id}/", { params: pathId(slug, id!) })),
  });
}

export function useEstimateRevision(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: [...salesKeys.estimates(slug), "revision", id],
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/estimate-revisions/{id}/", { params: pathId(slug, id!) })),
  });
}

export function useEstimateTimeline(slug: string, id: string) {
  return useQuery({
    queryKey: [...salesKeys.all(slug), "estimate-timeline", id],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/estimates/{id}/timeline/", { params: pathId(slug, id) })),
  });
}

export function useEstimateAttachments(slug: string, id: string) {
  return useQuery({
    queryKey: [...salesKeys.estimates(slug), "attachments", id],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/estimates/{id}/attachments/", { params: pathId(slug, id) })),
  });
}

type RevisionAnswer = Answer<"/api/t/{tenant_slug}/estimate-revisions/{id}/", "get">;

/** Every revision edit answers with the whole revision, which replaces the cached one. */
export function useRevisionAction(slug: string, revisionId: string) {
  const qc = useQueryClient();
  const key = [...salesKeys.estimates(slug), "revision", revisionId];
  return useMutation({
    meta: noErrorToast,
    mutationFn: (
      vars:
        | { action: "update"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/", "patch"> }
        | { action: "add-section"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/sections/", "post"> }
        | { action: "add-group"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/groups/", "post"> }
        | { action: "phasing"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/phasing/", "post"> }
        | { action: "answer"; selectionId: string; body: Body<"/api/t/{tenant_slug}/estimate-selections/{id}/answer/", "post"> }
        | { action: "add-line"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/lines/", "post"> }
        | { action: "reorder"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/reorder/", "post"> }
        | { action: "reprice"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/reprice/", "post"> }
        | { action: "drawing-apply"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/drawing-apply/", "post"> }
        | { action: "drawing-reference"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/drawing-reference/", "post"> }
        | { action: "submit"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/submit/", "post"> }
        | { action: "decide"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/decide/", "post"> }
        | { action: "issue" }
        | { action: "retax" }
        | { action: "withdraw"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/withdraw/", "post"> }
        | { action: "record-decision"; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/record-decision/", "post"> }
        | { action: "line"; lineId: string; body: Body<"/api/t/{tenant_slug}/estimate-lines/{id}/", "patch"> }
        | { action: "delete-line"; lineId: string }
        | { action: "adjust"; lineId: string; body: Body<"/api/t/{tenant_slug}/estimate-lines/{id}/adjust/", "post"> }
        | { action: "section"; sectionId: string; body: Body<"/api/t/{tenant_slug}/estimate-sections/{id}/", "patch"> }
        | { action: "delete-section" | "duplicate-section"; sectionId: string; withLines?: boolean },
    ): Promise<RevisionAnswer> => {
      const p = pathId(slug, revisionId);
      switch (vars.action) {
        case "update":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/estimate-revisions/{id}/", { params: p, body: vars.body }));
        case "add-section":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/sections/", { params: p, body: vars.body }));
        case "add-group":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/groups/", { params: p, body: vars.body }));
        case "phasing":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/phasing/", { params: p, body: vars.body }));
        case "retax":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/retax/", { params: p }));
        case "answer":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-selections/{id}/answer/", { params: pathId(slug, vars.selectionId), body: vars.body }));
        case "add-line":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/lines/", { params: p, body: vars.body }));
        case "reorder":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/reorder/", { params: p, body: vars.body }));
        case "reprice":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/reprice/", { params: p, body: vars.body }));
        case "drawing-apply":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/drawing-apply/", { params: p, body: vars.body }));
        case "drawing-reference":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/drawing-reference/", { params: p, body: vars.body }));
        case "submit":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/submit/", { params: p, body: vars.body }));
        case "decide":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/decide/", { params: p, body: vars.body }));
        case "issue":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/issue/", { params: p }));
        case "withdraw":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/withdraw/", { params: p, body: vars.body }));
        case "record-decision":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/record-decision/", { params: p, body: vars.body }));
        case "line":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/estimate-lines/{id}/", { params: pathId(slug, vars.lineId), body: vars.body }));
        case "delete-line":
          return unwrap(api.DELETE("/api/t/{tenant_slug}/estimate-lines/{id}/", { params: pathId(slug, vars.lineId) }));
        case "adjust":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-lines/{id}/adjust/", { params: pathId(slug, vars.lineId), body: vars.body }));
        case "section":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/estimate-sections/{id}/", { params: pathId(slug, vars.sectionId), body: vars.body }));
        case "delete-section":
          return unwrap(api.DELETE("/api/t/{tenant_slug}/estimate-sections/{id}/", { params: { ...pathId(slug, vars.sectionId), query: vars.withLines ? { with_lines: true } : {} } }));
        default:
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-sections/{id}/duplicate/", { params: pathId(slug, vars.sectionId) }));
      }
    },
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      qc.invalidateQueries({ queryKey: [...salesKeys.estimates(slug), "list"] });
      qc.invalidateQueries({ queryKey: [...salesKeys.estimates(slug), "detail"] });
      qc.invalidateQueries({ queryKey: salesKeys.jobs(slug) });
    },
  });
}

export function useDrawingPlan(slug: string, revisionId: string) {
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/drawing-plan/", "post">) =>
      unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/drawing-plan/", { params: pathId(slug, revisionId), body })),
  });
}

export function useEstimateAction(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (
      vars:
        | { action: "create"; body: Body<"/api/t/{tenant_slug}/estimates/", "post"> }
        | { action: "update"; id: string; body: Body<"/api/t/{tenant_slug}/estimates/{id}/", "patch"> }
        | { action: "add-phase"; id: string; body: Body<"/api/t/{tenant_slug}/estimates/{id}/phases/", "post"> }
        | { action: "phase"; id: string; phaseId: string; body: Body<"/api/t/{tenant_slug}/estimates/{id}/phases/{phase_id}/", "patch"> }
        | { action: "remove-phase"; id: string; phaseId: string }
        | { action: "link-job"; id: string; body: Body<"/api/t/{tenant_slug}/estimates/{id}/link-job/", "post"> }
        | { action: "revise"; id: string; body: Body<"/api/t/{tenant_slug}/estimates/{id}/revise/", "post"> }
        | { action: "discard"; revisionId: string }
        | { action: "link"; revisionId: string; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/links/", "post"> }
        | { action: "revoke-link"; id: string }
        | { action: "email"; revisionId: string; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/email/", "post"> }
        | { action: "record-delivery"; revisionId: string; body: Body<"/api/t/{tenant_slug}/estimate-revisions/{id}/record-delivery/", "post"> },
    ): Promise<unknown> => {
      switch (vars.action) {
        case "create":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimates/", { params: path(slug), body: vars.body }));
        case "update":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/estimates/{id}/", { params: pathId(slug, vars.id), body: vars.body }));
        case "link-job":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimates/{id}/link-job/", { params: pathId(slug, vars.id), body: vars.body }));
        case "add-phase":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimates/{id}/phases/", { params: pathId(slug, vars.id), body: vars.body }));
        case "phase":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/estimates/{id}/phases/{phase_id}/", { params: { path: { tenant_slug: slug, id: vars.id, phase_id: vars.phaseId } }, body: vars.body }));
        case "remove-phase":
          return unwrap(api.DELETE("/api/t/{tenant_slug}/estimates/{id}/phases/{phase_id}/", { params: { path: { tenant_slug: slug, id: vars.id, phase_id: vars.phaseId } } }));
        case "revise":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimates/{id}/revise/", { params: pathId(slug, vars.id), body: vars.body }));
        case "discard":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/discard/", { params: pathId(slug, vars.revisionId) }));
        case "link":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/links/", { params: pathId(slug, vars.revisionId), body: vars.body }));
        case "revoke-link":
          return unwrap(api.POST("/api/t/{tenant_slug}/proposal-links/{id}/revoke/", { params: pathId(slug, vars.id) }));
        case "email":
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/email/", { params: pathId(slug, vars.revisionId), body: vars.body }));
        default:
          return unwrap(api.POST("/api/t/{tenant_slug}/estimate-revisions/{id}/record-delivery/", { params: pathId(slug, vars.revisionId), body: vars.body }));
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: salesKeys.all(slug) }),
  });
}

// --- public proposals ---------------------------------------------------------------------------------
export function usePublicProposal(token: string) {
  return useQuery({
    queryKey: ["public-proposal", token],
    enabled: token.length >= 20,
    queryFn: () => unwrap(api.POST("/api/public/proposals/view/", { body: { token } })),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function usePublicDecision(token: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: Omit<Body<"/api/public/proposals/decide/", "post">, "token">) => unwrap(api.POST("/api/public/proposals/decide/", { body: { ...body, token } })),
    onSuccess: (data) => qc.setQueryData(["public-proposal", token], data),
  });
}
